import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import OpportunityAPI from 'dashboard/api/opportunities';
import { createStore } from 'dashboard/store/storeFactory';
import { throwErrorMessage } from 'dashboard/store/utils/api';

const camelizeOpportunity = data => camelcaseKeys(data || {}, { deep: true });

const normalizeMeta = meta => ({
  ...camelcaseKeys(meta || {}),
  totalCount: Number(meta?.total_count || meta?.totalCount || 0),
  page: Number(meta?.page || meta?.current_page || 1),
});

const buildOpportunityRequestPayload = opportunityAttrs => ({
  opportunity: snakecaseKeys(opportunityAttrs, { deep: true }),
});

export const useOpportunitiesStore = createStore({
  name: 'opportunities',
  type: 'pinia',
  API: OpportunityAPI,
  state: () => ({
    boardRecords: [],
  }),

  getters: {
    getOpportunitiesList: state => state.records,
    getBoardRecords: state => state.boardRecords,
  },

  actions: () => ({
    setMeta(meta) {
      this.meta = normalizeMeta(meta);
    },

    upsertOpportunityRecord(record) {
      const index = this.records.findIndex(r => r.id === record.id);
      if (index === -1) this.records.push(record);
      else this.records[index] = record;

      const boardIndex = this.boardRecords.findIndex(r => r.id === record.id);
      if (boardIndex !== -1) this.boardRecords[boardIndex] = record;
    },

    async get({ page = 1, sort = 'name', stage, ownerId, companyId } = {}) {
      this.setUIFlag({ fetchingList: true });
      try {
        const {
          data: { payload, meta },
        } = await OpportunityAPI.get({ page, sort, stage, ownerId, companyId });
        this.records = camelizeOpportunity(payload);
        this.setMeta(meta);
        return this.records;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingList: false });
      }
    },

    async getBoard(params = {}) {
      this.setUIFlag({ fetchingBoard: true });
      try {
        const {
          data: { payload },
        } = await OpportunityAPI.getBoard(params);
        this.boardRecords = camelizeOpportunity(payload);
        return this.boardRecords;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingBoard: false });
      }
    },

    async show(id) {
      this.setUIFlag({ fetchingItem: true });
      try {
        const {
          data: { payload },
        } = await OpportunityAPI.show(id);
        const opportunity = camelizeOpportunity(payload);
        this.upsertOpportunityRecord(opportunity);
        return opportunity;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ fetchingItem: false });
      }
    },

    async create(opportunityAttrs) {
      this.setUIFlag({ creatingItem: true });
      try {
        const {
          data: { payload },
        } = await OpportunityAPI.create(
          buildOpportunityRequestPayload(opportunityAttrs)
        );
        const opportunity = camelizeOpportunity(payload);
        this.upsertOpportunityRecord(opportunity);
        this.boardRecords.push(opportunity);
        return opportunity;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ creatingItem: false });
      }
    },

    async update({ id, ...opportunityAttrs }) {
      this.setUIFlag({ updatingItem: true });
      try {
        const {
          data: { payload },
        } = await OpportunityAPI.update(
          id,
          buildOpportunityRequestPayload(opportunityAttrs)
        );
        const opportunity = camelizeOpportunity(payload);
        this.upsertOpportunityRecord(opportunity);
        return opportunity;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ updatingItem: false });
      }
    },

    async moveStage(id, stage) {
      this.setUIFlag({ movingStage: true });
      try {
        const {
          data: { payload },
        } = await OpportunityAPI.update(id, { opportunity: { stage } });
        const opportunity = camelizeOpportunity(payload);
        this.upsertOpportunityRecord(opportunity);
        return opportunity;
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ movingStage: false });
      }
    },

    async delete(id) {
      this.setUIFlag({ deletingItem: true });
      try {
        await OpportunityAPI.delete(id);
        this.records = this.records.filter(r => r.id !== Number(id));
        this.boardRecords = this.boardRecords.filter(r => r.id !== Number(id));
        return Number(id);
      } catch (error) {
        return throwErrorMessage(error);
      } finally {
        this.setUIFlag({ deletingItem: false });
      }
    },
  }),
});
